import { OrgTab, type OrgTabConfig, type OrgTabType } from './constants';

export interface CreateOrganizationTabManagerParams {
  loadingStates: {
    isLoadingOrganization: boolean;
    isLoadingApiKey: boolean;
    isLoadingMembers: boolean;
    isLoadingRoles: boolean;
  };
  errorStates: {
    isOrganizationError: boolean;
  };
  flags: {
    shadowAiEnabled: boolean;
    organizationDashboardEnabled: boolean;
    agentCommandVisibilityEnabled: boolean;
    secureAiCodingEnabled: boolean;
    // KIR-3800. Optional so existing callers/tests that predate Security Groups
    // keep compiling; absent is treated as off, matching the flag's default.
    securityGroupsEnabled?: boolean;
  };
  orgMeta: {
    organizationId: string;
    organizationMembersCount: number;
    isSetup: boolean;
    canViewAdminContent: boolean;
  };
  tabsConfig: Partial<OrgTabConfig>;
}

type OrgTabManager = {
  isLoading: boolean;
  isError: boolean;
  isTabVisible: (tab: OrgTabType) => boolean;
  getVisibleTabs: () => OrgTabType[];
  getDefaultTab: () => OrgTabType;
  canMountRoutes: boolean;
};

export const createOrganizationRenderManager = ({
  flags,
  loadingStates,
  orgMeta,
  tabsConfig,
  errorStates,
}: CreateOrganizationTabManagerParams): OrgTabManager => {
  const isLoading =
    loadingStates.isLoadingOrganization ||
    loadingStates.isLoadingApiKey ||
    loadingStates.isLoadingMembers ||
    loadingStates.isLoadingRoles;

  const isError =
    (errorStates.isOrganizationError || !orgMeta.organizationId) && !isLoading;

  const canMountRoutes = !isLoading && !isError;

  const isTabVisible = (tab: OrgTabType): boolean => {
    if (isLoading || isError) return false;
    if (!tabsConfig[tab]) return false;

    // Feature flags
    if (tab === OrgTab.ShadowAI && !flags.shadowAiEnabled) return false;
    if (tab === OrgTab.AgentsActivity && !flags.agentCommandVisibilityEnabled)
      return false;
    if (tab === OrgTab.SecurityGroups && !flags.securityGroupsEnabled)
      return false;
    // KIR-3816 / KIR-3925: all three AI AppSec pages ride the
    // ai_instruction_catalog flag.
    if (
      (tab === OrgTab.SecureAiCoding ||
        tab === OrgTab.ThreatModeling ||
        tab === OrgTab.LlmVulnDiscovery) &&
      !flags.secureAiCodingEnabled
    )
      return false;

    const isSimpleUser =
      !flags.organizationDashboardEnabled ||
      orgMeta.organizationMembersCount < 2 ||
      !orgMeta.canViewAdminContent;

    if (tab === OrgTab.Dashboard && isSimpleUser) {
      return false;
    }

    return true;
  };

  const getVisibleTabs = (): OrgTabType[] => {
    return Object.keys(tabsConfig).filter(tab =>
      isTabVisible(tab as OrgTabType)
    ) as OrgTabType[];
  };

  const getDefaultTab = (): OrgTabType => {
    const { isSetup } = orgMeta;
    if (isSetup) {
      if (isTabVisible(OrgTab.Dashboard)) return OrgTab.Dashboard;
      if (isTabVisible(OrgTab.Alerts)) return OrgTab.Alerts;
      return OrgTab.Install;
    }
    return OrgTab.Install;
  };

  return {
    isTabVisible,
    canMountRoutes,
    getDefaultTab,
    getVisibleTabs,
    isError,
    isLoading,
  };
};
